import { keepPreviousData, queryOptions, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { companySlug } from "@/lib/companySlug";

export interface FeedAgreement {
  id: string;
  external_id: string;
  company_name: string;
  contract_name: string | null;
  logo_url: string | null;
  area: string;
  consumption: number;
  contract_type: string;
  comparative_price: number | null;
  price_ore_kwh: number | null;
  monthly_price: number | null;
  markup_ore: number | null;
  monthly_fee: number | null;
  binding_text: string | null;
  notice_period_text: string | null;
  terms_url: string | null;
  discount_text: string | null;
  energy_sources: string | null;
  usps: string[] | null;
}

const FIELDS =
  "id, external_id, company_name, contract_name, logo_url, area, consumption, contract_type, comparative_price, price_ore_kwh, monthly_price, markup_ore, monthly_fee, binding_text, notice_period_text, terms_url, discount_text, energy_sources, usps";

const STALE_TIME = 5 * 60 * 1000;

/** Jämförbar nyckel: gemener, utan tecken och utan bolagssuffix (SE, AB ...). */
const matchKey = (value: string | null | undefined) =>
  companySlug(value)
    .replace(/-/g, "")
    .replace(/(se|sverige|ab|nordic)$/, "");

/** Närmaste tillgängliga förbrukningsnivå i feeden (2000/5000/20000). */
const LEVELS = [2000, 5000, 20000];
const nearestLevel = (consumption: number) =>
  LEVELS.reduce((best, level) =>
    Math.abs(level - consumption) < Math.abs(best - consumption) ? level : best,
  );

/** Hämtar bolagets feedavtal för ett elområde och en förbrukningsnivå. */
export async function fetchCompanyFeedAgreements(
  slug: string,
  name: string | null | undefined,
  area: string,
  consumption: number,
): Promise<FeedAgreement[]> {
  // Feeden innehåller tusentals avtal – förfiltrera på bolagsnamn i databasen
  // (PostgREST returnerar max 1000 rader per anrop).
  const token = String(name ?? slug ?? "")
    .split(/[\s-]+/)
    .filter((t) => t.length > 2)[0];

  let query = supabase
    .from("agreement_feed_items")
    .select(FIELDS)
    .eq("is_active", true)
    .eq("is_private", true)
    .eq("area", area)
    .eq("consumption", nearestLevel(consumption));
  if (token) query = query.ilike("company_name", `%${token}%`);

  const { data } = await query.limit(200);

  const rows = (data as unknown as FeedAgreement[] | null) ?? [];
  const keys = [slug, name].filter(Boolean).map((v) => matchKey(v as string));
  const matched = rows.filter((row) => {
    if (companySlug(row.company_name) === slug) return true;
    const key = matchKey(row.company_name);
    return keys.some(
      (k) => k.length > 3 && (k === key || k.startsWith(key) || key.startsWith(k)),
    );
  });

  // Ett avtal per externt ID, billigast först.
  const unique = new Map<string, FeedAgreement>();
  for (const row of matched) if (!unique.has(row.external_id)) unique.set(row.external_id, row);
  return [...unique.values()].sort(
    (a, b) => (a.comparative_price ?? Infinity) - (b.comparative_price ?? Infinity),
  );
}

export const companyFeedAgreementsQueryOptions = (
  slug: string,
  name: string | null | undefined,
  area: string,
  consumption: number,
) =>
  queryOptions({
    queryKey: ["company-feed-agreements", slug, area, nearestLevel(consumption)],
    queryFn: () => fetchCompanyFeedAgreements(slug, name, area, consumption),
    staleTime: STALE_TIME,
    // Behåll föregående elområdes avtal medan nästa hämtas – annars hoppar layouten.
    placeholderData: keepPreviousData,
  });

/**
 * Hämtar elbolagets avtal från Avtals-API-feeden för valt elområde. Används på
 * elbolagens landningssidor som komplement till våra egna kampanjer – feedade
 * avtal visas alltid efter annonsörernas kampanjer. `initialData` kan matas
 * från en route loader (SSR) för att undvika layout-hopp.
 */
export const useCompanyFeedAgreements = (
  slug: string | null | undefined,
  name?: string | null,
  area = "SE3",
  consumption = 5000,
  initialData?: FeedAgreement[],
) => {
  const { data, isLoading } = useQuery({
    ...companyFeedAgreementsQueryOptions(slug ?? "", name, area, consumption),
    enabled: !!slug,
    ...(initialData !== undefined ? { initialData } : {}),
  });

  return { agreements: data ?? [], loading: !!slug && isLoading };
};
